import React, { memo, useMemo } from "react";
import {
  extractActionsFromStop,
  resolveStopName,
} from "Components/DeployedMissionList/components/MissionItem/TimelineContainer";

import {
  MissionDetailsTimelineWrapper,
  MissionCycleTitle,
  MissionStopsContainer,
  MissionTimelineVerticalLine,
  MissionStopRow,
  MissionStopIcon,
  MissionStopName,
  MissionStopActionsList,
  MissionStopActionItem,
  MissionTimelineEmptyState,
  MissionCycleContainer,
  CycleTitleLabel,
  CycleNumbersContainer,
  CycleCurrentNumber,
  CycleSeparator,
  CycleTotalNumber,
  CycleTitleContent,
  CycleTitleWrapper,
} from "./styles";
import { ReactComponent as StopShapeIcon } from "./assets/StopShape.svg";
import { ReactComponent as StopShapeLightIcon } from "./assets/StopShapeLight.svg";
import { MissionDetailsTimelineProps, CycleData } from "../../types";

// Mock function for getting stop names - in real implementation this would come from a store
const getStopName = (stopId: string): string => {
  return stopId || "Unknown Stop";
};

export const MissionDetailsTimeline = memo(
  ({
    stops,
    totalLoops,
    passedLoops,
    passedStops,
  }: MissionDetailsTimelineProps) => {
    // Validate inputs
    if (!stops || !Array.isArray(stops) || stops.length === 0) {
      return (
        <MissionDetailsTimelineWrapper data-test-id="show-mission-details-timeline">
          <MissionStopsContainer>
            <MissionTimelineEmptyState data-test-id="show-mission-details-empty-state">
              No stops planned for this mission
            </MissionTimelineEmptyState>
          </MissionStopsContainer>
        </MissionDetailsTimelineWrapper>
      );
    }

    const adjustedPassedLoops =
      passedStops === stops.length && passedLoops > 0
        ? passedLoops - 1
        : passedLoops;

    if (!totalLoops || totalLoops < 1) {
      return (
        <MissionDetailsTimelineWrapper>
          <MissionStopsContainer>
            <MissionTimelineEmptyState>
              Invalid number of cycles
            </MissionTimelineEmptyState>
          </MissionStopsContainer>
        </MissionDetailsTimelineWrapper>
      );
    }

    // Optimize cycles calculation with useMemo
    const cycles: CycleData[] = useMemo(() => {
      return Array.from({ length: totalLoops }, (_, cycleIndex) => ({
        cycleNumber: cycleIndex + 1,
        totalCycles: totalLoops,
        stops: stops, // Each cycle shows all stops
      }));
    }, [totalLoops, stops]);

    return (
      <MissionDetailsTimelineWrapper data-test-id="show-mission-details-timeline">
        {cycles.map((cycle) => (
          <MissionCycleContainer
            key={cycle.cycleNumber}
            data-test-id={`show-mission-details-cycle-${cycle.cycleNumber}`}
          >
            <MissionCycleTitle
              data-test-id={`show-mission-details-cycle-title-${cycle.cycleNumber}`}
            >
              <CycleTitleWrapper>
                <CycleTitleContent>
                  <CycleTitleLabel>Cycle:</CycleTitleLabel>
                  <CycleNumbersContainer>
                    <CycleCurrentNumber
                      data-test-id={`show-mission-details-cycle-current-${cycle.cycleNumber}`}
                    >
                      {cycle.cycleNumber}
                    </CycleCurrentNumber>
                    <CycleSeparator />
                    <CycleTotalNumber
                      data-test-id={`show-mission-details-cycle-total-${cycle.cycleNumber}`}
                    >
                      {cycle.totalCycles}
                    </CycleTotalNumber>
                  </CycleNumbersContainer>
                </CycleTitleContent>
              </CycleTitleWrapper>
            </MissionCycleTitle>
            <MissionStopsContainer>
              <MissionTimelineVerticalLine />
              {cycle.stops.map((stop, stopIndex) => {
                const stopName = resolveStopName(stop, getStopName);
                const actions = extractActionsFromStop(stop);

                // Calculate if this stop should be pending
                // Stops are pending if:
                // 1. Current cycle is higher than adjustedPassedLoops, OR
                // 2. Current cycle equals adjustedPassedLoops + 1 AND stopIndex + 1 > passedStops
                const isPending =
                  cycle.cycleNumber > adjustedPassedLoops + 1 ||
                  (cycle.cycleNumber === adjustedPassedLoops + 1 &&
                    stopIndex + 1 > passedStops);

                return (
                  <MissionStopRow
                    key={`${stopName}-${stopIndex}`}
                    data-test-id={`show-mission-details-stop-${cycle.cycleNumber}-${stopIndex}`}
                  >
                    <MissionStopIcon
                      role="img"
                      aria-label={`Stop ${stopName} icon`}
                      data-test-id={`show-mission-details-stop-icon-${cycle.cycleNumber}-${stopIndex}`}
                    >
                      {isPending ? <StopShapeLightIcon /> : <StopShapeIcon />}
                    </MissionStopIcon>
                    <MissionStopName
                      data-test-id={`show-mission-details-stop-name-${cycle.cycleNumber}-${stopIndex}`}
                    >
                      {stopName}
                    </MissionStopName>
                    <MissionStopActionsList
                      role="list"
                      aria-label={`Actions for ${stopName}`}
                      data-test-id={`show-mission-details-stop-actions-${cycle.cycleNumber}-${stopIndex}`}
                    >
                      <MissionStopActionItem>
                        {actions.map((action, actionIndex) => (
                          <div
                            key={actionIndex}
                            role="listitem"
                            data-test-id={`show-mission-details-action-${cycle.cycleNumber}-${stopIndex}-${actionIndex}`}
                          >
                            {action}
                          </div>
                        ))}
                      </MissionStopActionItem>
                    </MissionStopActionsList>
                  </MissionStopRow>
                );
              })}
            </MissionStopsContainer>
          </MissionCycleContainer>
        ))}
      </MissionDetailsTimelineWrapper>
    );
  }
);
